import "./Portfolio.scss";

import { gsap } from "gsap";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Section from "../../../components/Section/Section";
import SectionHeader from "../../../components/SectionHeader/SectionHeader";
import ShowMoreButton from "../../../components/ShowMoreButton/ShowMoreButton";
import {
  FEATURED_PROJECTS,
  MORE_PROJECTS,
  type Project,
  type ProjectBase,
} from "../../../config/projects";
import ProjectCard from "./ProjectCard/ProjectCard";

type ProjectTranslation = {
  title: string;
  description: string;
  statusLabel: string;
};

const Portfolio = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const translations = t("portfolio.projects", {
    returnObjects: true,
  }) as Record<string, ProjectTranslation>;

  const mergeProject = (base: ProjectBase): Project => ({
    ...base,
    ...translations[base.id],
  });

  const featuredProjects = FEATURED_PROJECTS.map(mergeProject);
  const moreProjects = MORE_PROJECTS.map(mergeProject);

  useEffect(() => {
    if (!isOpen || !gridRef.current) return;

    const cards = gridRef.current.querySelectorAll(".moreCard");
    if (!cards.length) return;

    gsap.fromTo(
      cards,
      { autoAlpha: 0, y: 8, scale: 0.996 },
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 0.2,
        stagger: 0.02,
        ease: "none",
      },
    );
  }, [isOpen]);

  return (
    <Section id="portfolio" className="portfolio">
      <SectionHeader
        tag={t("portfolio.tag")}
        title={t("portfolio.title")}
        subtitle={t("portfolio.subtitle")}
      />

      <div ref={gridRef} className={`projectGrid fi ${isOpen ? "open" : ""}`}>
        {featuredProjects.map((project, index) => (
          <ProjectCard
            key={project.id}
            project={project}
            displayNumber={String(index + 1).padStart(2, "0")}
          />
        ))}
        {moreProjects.map((project, index) => (
          <ProjectCard
            key={project.id}
            project={project}
            className="moreCard"
            displayNumber={String(featuredProjects.length + index + 1).padStart(
              2,
              "0",
            )}
          />
        ))}
      </div>

      <ShowMoreButton
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        moreLabel={t("portfolio.view_more", { count: moreProjects.length })}
        lessLabel={t("portfolio.view_less")}
      />
    </Section>
  );
};

export default Portfolio;
